/**
 * Process-wide error sink.
 *
 * Low-tier packages (ui-core's `ErrorBoundary`) cannot import the reporter that
 * lives in a higher tier, so the reporter REGISTERS itself here when it
 * initialises and boundaries report through {@link reportCaughtError}. When no
 * sink is registered (tests, SSR, apps that never init a reporter) reporting is
 * a no-op.
 */

import { normalizeError } from "./normalize";
import type { ErrorCategory, ErrorSeverity } from "./types";

export interface SinkOptions {
  category?: ErrorCategory;
  metadata?: Record<string, unknown>;
}

export type ErrorSink = (
  message: string,
  stack: string | undefined,
  severity: ErrorSeverity,
  options?: SinkOptions
) => unknown;

let sink: ErrorSink | null = null;

/** Register (or clear, with `null`) the process-wide sink. */
export function setErrorSink(next: ErrorSink | null): void {
  sink = next;
}

export function getErrorSink(): ErrorSink | null {
  return sink;
}

export interface CaughtErrorContext {
  /** React component stack from `componentDidCatch`. */
  componentStack?: string | null;
  /** Which boundary caught it, e.g. `ui-core` or `shell:finance`. */
  boundary?: string;
  /** Human label for what the boundary protects. */
  label?: string;
  metadata?: Record<string, unknown>;
}

const MAX_COMPONENT_STACK = 2000;

/**
 * Report an error caught by a boundary. Mirrors
 * `captureError(String(error), error.stack, "error", { category: "page", … })`.
 * Never throws and never rejects.
 */
export function reportCaughtError(error: unknown, context: CaughtErrorContext = {}): void {
  const current = sink;
  if (!current) return;
  try {
    const normalized = normalizeError(error);
    const message = error instanceof Error ? String(error) : `${normalized.name}: ${normalized.message}`;
    const metadata: Record<string, unknown> = {
      ...(context.boundary ? { boundary: context.boundary } : {}),
      ...(context.label ? { boundary_label: context.label } : {}),
      ...(context.componentStack ? { component_stack: context.componentStack.slice(0, MAX_COMPONENT_STACK) } : {}),
      ...context.metadata,
    };
    const result = current(message, normalized.stack, "error", { category: "page", metadata });
    if (result && typeof (result as Promise<unknown>).catch === "function") {
      void (result as Promise<unknown>).catch(() => undefined);
    }
  } catch {
    // Telemetry must never break the boundary.
  }
}
